<template>
  <div class="ms-cell-editor combobox comboboxtree">
    <div
      v-show="!isSelected || readOnly"
      :class="{
        'edit-disabled': readOnly,
        'ms-editor-text': editable,
        hasError: errorProvider.isValid,
        'display-error': errorMessage && !isEditor,
        'error-border': errorMessage,
      }"
    >
      <a v-show="!isEditor" class="editor-click" @click="$emit('editorClick', $event, col, colIndex)">
        <span
          class="editor-display"
          :title="[
            dataRow[col.dataField]
              ? $ms.format.formatData(dataRow[col.dataField], {
                  formatType: col.formatType,
                  enumName: col.enum,
                })
              : '',
          ]"
          >{{
            dataRow[col.dataField]
              ? $ms.format.formatData(dataRow[col.dataField], {
                  formatType: col.formatType,
                  enumName: col.enum,
                })
              : ''
          }}</span
        >
      </a>

      <ms-combobox-tree
        v-if="isEditor"
        v-model="dataRow[modelValueField]"
        v-model:display="dataRow[col.dataField]"
        :data="comboData"
        :columns="propsData.columns"
        :display-field="propsData.displayField"
        :value-field="propsData.valueField"
        :id-property="propsData.idField ?? propsData.valueField"
        :parent-id-property="propsData.parentField ?? 'parent_id'"
        :init-text="dataRow[col.dataField]"
        :quick-add="propsData.quickAdd"
        @selected="onSelectValue"
        ref="msComboboxTree"
        :quick-search="propsData.quickSearch"
        :query-mode="propsData.queryMode || 'local'"
        :remote-filter="propsData.queryMode === 'remote' ? true : false"
        :dropdown-width="propsData.dropdownWidth || 0"
        :is-grid-editor="true"
        :rules="propsData.rules || [{name: $ms.constant.ValidateRule.ForceSelection}]"
        v-on="listeners"
        :preventPaste="allowCopyFromExcel"
        :quick-search-param="propsData.quickSearchParam"
      ></ms-combobox-tree>

      <div v-show="errorMessage && !isEditor" v-tooltip="errorMessage" class="icon24 error"></div>
    </div>
    <div v-show="isSelected && !readOnly" ref="editor" class="ms-editor control-cell"></div>
  </div>
</template>

<script setup></script>
<script>
import {computed, watch, nextTick, ref, isRef, getCurrentInstance, inject} from 'vue';
import {useCellEditor} from './CellEditorSetup.js';
export default {
  name: 'MsCellComboboxTree',
  props: {
    col: {},
    dataRow: {},
    rowIndex: {
      default: 0,
      type: Number,
    },
    colIndex: {
      default: 0,
      type: Number,
    },
    // Thuộc tính để cấu hình cho phép sửa trên grid hay không
    editable: {
      default: true,
      type: Boolean,
    },
    rowSelected: {
      default: null,
    },
    readOnly: {
      default: false,
      type: Boolean,
    },
    // có cho phép show text validate bên dưới cell k
    isShowText: {
      type: Boolean,
      default: false,
    },
    propsData: {
      type: Object,
      default: () => {},
    },
    rules: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['editorClick', 'comboboxLoadData', 'msKeydown', 'selectValue', 'pasteInput', 'update:modelValue'],
  setup(props, {emit}) {
    const {proxy} = getCurrentInstance();
    const comboData = computed(() => {
      if (props.propsData.queryMode === 'remote') {
        return props.propsData.data[props.col.dataField];
      }
      return props.propsData.data;
    });
    const {
      isEditor,
      errorProvider,
      isSelected,
      listeners,
      activeCell,
      inactiveCell,
      findEditor,
      getValue,
      errorMessage,
      validate,
      clearValidate,
    } = useCellEditor({
      props,
      emit,
    });

    watch(isEditor, newVal => {
      if (newVal) {
        nextTick(() => {
          findEditor();
        });
      }
    });

    const allowCopyFromExcel = inject('allowCopyFromExcel');

    const modelValueField = props.propsData.modelValueField || props.propsData.valueField;
    const msCombobox = ref({});

    const getMetaData = () => {
      return msCombobox.value?.internalSelectedItem ? {...msCombobox.value.internalSelectedItem} : {};
    };

    const onSelectValue = data => {
      proxy.$emit('selectValue', data);
    };

    /**
     * Gán giá trị cho cell
     */
    const setValue = value => {
      const me = proxy;

      if (me.readOnly) {
        return;
      }

      if (isEditor.value && typeof me.$refs.msCombobox?.setInternalText == 'function') {
        me.$refs.msCombobox.setInternalText(value);
      }

      let find = me.comboData.find(x => x[me.propsData.displayField || me.col.dataField]?.trim() == value?.trim());
      if (find) {
        me.dataRow[me.col.dataField] = value;
        me.dataRow[modelValueField] = find[me.propsData.valueField || modelValueField];
      } else {
        me.dataRow[me.col.dataField] = null;
        me.dataRow[modelValueField] = null;
      }
      me.$emit('update:modelValue', me.dataRow[me.col.dataField]);

      return find;
    };

    return {
      isEditor,
      errorProvider,
      isSelected,
      listeners,
      activeCell,
      inactiveCell,
      findEditor,
      getValue,
      errorMessage,
      validate,
      clearValidate,
      modelValueField,
      comboData,
      getMetaData,
      msCombobox,
      onSelectValue,
      setValue,
      allowCopyFromExcel,
    };
  },
};
</script>

<style lang="scss" scoped></style>
